import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AnimatePresence } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import AdminFilterBar, { AdminSearchField } from '../components/ui/AdminFilterBar';
import AdminPagination from '../components/ui/AdminPagination';
import { AdminLoadingState, AdminEmptyState } from '../components/ui/AdminStates';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import './AdminRegister.css';

interface Admin {
  id_user: number;
  cpf: string;
  name: string | null;        // ⬅️ agora tipado para receber o nome do GET
  email: string;
  special_services: number;
  user_level: string;
}

const PAGE_SIZE = 10;

// helper: mostra vazio se name vier null/blank
const displayName = (name: string | null) => (name?.trim() ?? '');

const AdminPoloRegister: React.FC = () => {
  const { adminToken } = useAdminAuth();
  const navigate = useNavigate();

  const [formDataAdmin, setFormDataAdmin] = useState({
    cpf: '',
    name: '',
    email: '',
    password: '',
    special_services: false,   // boolean, conforme seu exemplo de POST
    user_level: 'admin',
  });

  const [editAdminId, setEditAdminId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState({ user_level: 'admin' });

  const [errorAdmin, setErrorAdmin] = useState<string | null>(null);
  const [successAdmin, setSuccessAdmin] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ cpf?: string; email?: string; name?: string }>({});

  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [adminPage, setAdminPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Helper: interpreta erro 500 de duplicidade
  function parseDuplicateError(data: any) {
    const raw = typeof data === 'string' ? data : JSON.stringify(data);
    const errorStr = (data?.error || raw || '').toString();

    const isCpfDup = /key\s+'users\.cpf'/i.test(errorStr);
    const isEmailDup = /key\s+'users\.email'/i.test(errorStr);

    if (isCpfDup) {
      return {
        global: 'Já existe um administrador com este CPF.',
        fieldErrors: { cpf: 'CPF já cadastrado.' },
      };
    }
    if (isEmailDup) {
      return {
        global: 'Já existe um administrador com este e-mail.',
        fieldErrors: { email: 'E-mail já cadastrado.' },
      };
    }
    return {
      global: data?.message || 'Falha ao registrar administrador.',
      fieldErrors: {},
    };
  }

  // API base
  const api = axios.create({
    baseURL: 'https://prevcenso.com/backend/api',
    timeout: 15000,
  });
  api.interceptors.request.use((cfg) => {
    if (adminToken) cfg.headers.Authorization = `Bearer ${adminToken}`;
    return cfg;
  });

  // GET admins (agora tipado com name)
  const fetchAdmins = useCallback(async () => {
    if (!adminToken) {
      navigate('/rj/admin-login');
      return;
    }
    try {
      const res = await api.get<{ admins: Admin[] }>('/auth/admins');
      setAdmins(res.data.admins || []);
    } catch (error: any) {
      console.error('Erro ao carregar administradores:', {
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
        url: error.config?.url,
      });
      setErrorAdmin('Falha ao carregar administradores.');
    } finally {
      setLoadingAdmins(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, navigate]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  // PDF
  const handleGeneratePDF = () => {
    if (admins.length === 0) {
      alert('Nenhum administrador disponível para gerar o PDF.');
      return;
    }
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Censo Prev', 20, 20);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Lista de Administradores', 20, 30);

      autoTable(doc, {
        startY: 40,
        head: [['ID Admin', 'CPF', 'Nome', 'Email', 'Serviços Especiais', 'Nível de Usuário']], // ⬅️ inclui "Nome"
        body: admins.map((admin) => [
          admin.id_user.toString(),
          admin.cpf,
          displayName(admin.name),    // ⬅️ vazio quando null
          admin.email,
          admin.special_services.toString(),
          admin.user_level,
        ]),
        styles: { font: 'helvetica', fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [25, 85, 191], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        tableWidth: 'auto',
        margin: { top: 40, left: 20, right: 20 },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 25 },
          2: { cellWidth: 35 }, // nome
          3: { cellWidth: 40 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
        },
      });

      doc.save('relatorio_administradores.pdf');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Falha ao gerar o PDF. Verifique se jspdf e jspdf-autotable estão instalados.');
    }
  };

  const handleInputChangeAdmin = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    if (name === 'cpf' || name === 'email' || name === 'name') {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    setErrorAdmin(null);
    setSuccessAdmin(null);

    setFormDataAdmin({ ...formDataAdmin, [name]: newValue as any });
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData({ ...editFormData, [name]: value });
  };

  const handleSubmitAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    setErrorAdmin(null);
    setSuccessAdmin(null);
    setFieldErrors({});

    // validações simples
    if (!formDataAdmin.cpf || !formDataAdmin.name || !formDataAdmin.email || !formDataAdmin.password || !formDataAdmin.user_level) {
      setErrorAdmin('Todos os campos são obrigatórios.');
      if (!formDataAdmin.cpf) setFieldErrors((p) => ({ ...p, cpf: 'Informe o CPF.' }));
      if (!formDataAdmin.name) setFieldErrors((p) => ({ ...p, name: 'Informe o nome.' }));
      if (!formDataAdmin.email) setFieldErrors((p) => ({ ...p, email: 'Informe o e-mail.' }));
      setSubmitting(false);
      return;
    }

    // Normaliza CPF (só dígitos)
    const cpfNumerico = formDataAdmin.cpf.replace(/\D/g, '');

    try {
      // POST /auth/register conforme exemplo
      await api.post('/auth/register', {
        cpf: cpfNumerico,
        name: formDataAdmin.name,
        password: formDataAdmin.password,
        special_services: formDataAdmin.special_services, // boolean
        email: formDataAdmin.email,
        user_level: formDataAdmin.user_level,
      });

      setSuccessAdmin('Administrador registrado com sucesso!');
      setFormDataAdmin({
        cpf: '',
        name: '',
        email: '',
        password: '',
        special_services: false,
        user_level: 'admin',
      });
      await fetchAdmins();
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      console.error('Erro ao registrar administrador:', {
        status,
        url: err?.config?.url,
        method: err?.config?.method,
        requestBody: err?.config?.data,
        responseHeaders: err?.response?.headers,
        responseData: data,
      });

      if (status === 500) {
        const parsed = parseDuplicateError(data);
        setErrorAdmin(parsed.global || 'Erro interno.');
        setFieldErrors(parsed.fieldErrors || {});
        if (parsed.fieldErrors?.cpf) document.getElementById('cpf')?.focus();
        else if (parsed.fieldErrors?.email) document.getElementById('email')?.focus();
      } else if (status === 400 || status === 409) {
        setErrorAdmin(data?.detail || data?.message || 'Dados inválidos para registro.');
      } else if (status === 401 || status === 403) {
        setErrorAdmin('Sem permissão para registrar administradores.');
      } else {
        setErrorAdmin('Falha ao registrar administrador. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminToken || !editAdminId) {
      navigate('/rj/admin-login');
      return;
    }

    const adminExists = admins.find((admin) => admin.id_user === editAdminId);
    if (!adminExists) {
      setErrorAdmin('Administrador não encontrado. Tente novamente.');
      return;
    }

    try {
      await api.put(`/auth/admins/${editAdminId}/level`, {
        user_level: editFormData.user_level,
      });
      setSuccessAdmin('Nível de usuário atualizado com sucesso!');
      setEditAdminId(null);
      setEditFormData({ user_level: 'admin' });
      await fetchAdmins();
    } catch (error: any) {
      setErrorAdmin(
        error.response?.data?.message ||
        `Falha ao atualizar nível de usuário para ID ${editAdminId}. Verifique se o administrador existe e tente novamente.`
      );
    }
  };

  const handleDeleteAdmin = async (adminId: number) => {
    if (!adminToken) {
      navigate('/rj/admin-login');
      return;
    }
    if (!window.confirm('Tem certeza que deseja apagar este administrador?')) return;

    try {
      await api.delete(`/auth/admins/${adminId}`);
      setSuccessAdmin('Administrador apagado com sucesso!');
      await fetchAdmins();
      if (pagedAdmins.length === 1 && adminPage > 1) setAdminPage(adminPage - 1);
    } catch (error: any) {
      setErrorAdmin(error.response?.data?.message || 'Falha ao apagar administrador. Tente novamente.');
    }
  };

  const startEditing = (admin: Admin) => {
    setEditAdminId(admin.id_user);
    setEditFormData({ user_level: admin.user_level });
  };

  const cancelEditing = () => {
    setEditAdminId(null);
    setEditFormData({ user_level: 'admin' });
    setErrorAdmin(null);
    setFieldErrors({});
  };

  // Filtro de busca (CPF, nome ou e-mail)
  const filteredAdmins = admins.filter((a) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      a.cpf.toLowerCase().includes(q) ||
      displayName(a.name).toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q)
    );
  });

  const totalAdminPages = Math.ceil(filteredAdmins.length / PAGE_SIZE) || 1;
  const pagedAdmins = filteredAdmins.slice((adminPage - 1) * PAGE_SIZE, adminPage * PAGE_SIZE);

  const chips = [
    searchTerm && { key: 'search', label: `Busca: ${searchTerm}`, onRemove: () => setSearchTerm('') },
  ].filter(Boolean) as { key: string; label: string; onRemove: () => void }[];

  return (
    <AdminLayout title="Registro de Administradores">
      <section className="admin-card">
        <div className="admin-card__head">
          <h2>Registrar Novo Administrador</h2>
        </div>
        <form onSubmit={handleSubmitAdmin} className="admin-form-grid" noValidate>
          <div className="admin-form-group">
            <label htmlFor="cpf">CPF</label>
            <input
              id="cpf"
              type="text"
              name="cpf"
              placeholder="CPF"
              value={formDataAdmin.cpf}
              onChange={handleInputChangeAdmin}
              className="admin-input"
              required
              inputMode="numeric"
              autoComplete="off"
            />
            {fieldErrors.cpf && <small>{fieldErrors.cpf}</small>}
          </div>

          <div className="admin-form-group">
            <label htmlFor="name">Nome</label>
            <input
              id="name"
              type="text"
              name="name"
              placeholder="Nome"
              value={formDataAdmin.name}
              onChange={handleInputChangeAdmin}
              className="admin-input"
              required
              autoComplete="off"
            />
            {fieldErrors.name && <small>{fieldErrors.name}</small>}
          </div>

          <div className="admin-form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="Email"
              value={formDataAdmin.email}
              onChange={handleInputChangeAdmin}
              className="admin-input"
              required
              autoComplete="off"
            />
            {fieldErrors.email && <small>{fieldErrors.email}</small>}
          </div>

          <div className="admin-form-group">
            <label htmlFor="password">Senha</label>
            <input
              id="password"
              type="password"
              name="password"
              placeholder="Senha"
              value={formDataAdmin.password}
              onChange={handleInputChangeAdmin}
              className="admin-input"
              required
              autoComplete="new-password"
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="user_level">Nível de Usuário</label>
            <select
              id="user_level"
              name="user_level"
              value={formDataAdmin.user_level}
              onChange={handleInputChangeAdmin}
              className="admin-select"
              required
            >
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>

          <AnimatePresence>
            {errorAdmin && <Alert variant="error">{errorAdmin}</Alert>}
            {successAdmin && <Alert variant="success">{successAdmin}</Alert>}
          </AnimatePresence>

          <div className="admin-form-actions">
            <Button type="submit" loading={submitting} disabled={submitting}>
              Registrar Administrador
            </Button>
          </div>
        </form>
      </section>

      {editAdminId && (
        <section className="admin-card">
          <div className="admin-card__head">
            <h2>Editar Nível de Usuário</h2>
          </div>
          <form onSubmit={handleEditAdmin} className="admin-form-grid">
            <div className="admin-form-group">
              <label htmlFor="edit_user_level">Nível de Usuário</label>
              <select
                id="edit_user_level"
                name="user_level"
                value={editFormData.user_level}
                onChange={handleEditInputChange}
                className="admin-select"
                required
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <AnimatePresence>
              {errorAdmin && <Alert variant="error">{errorAdmin}</Alert>}
              {successAdmin && <Alert variant="success">{successAdmin}</Alert>}
            </AnimatePresence>
            <div className="admin-form-actions">
              <Button type="submit">Salvar Alterações</Button>
              <Button type="button" variant="ghost" onClick={cancelEditing}>
                Cancelar
              </Button>
            </div>
          </form>
        </section>
      )}

      <section className="admin-card">
        <div className="admin-card__head">
          <h2>Administradores Registrados</h2>
          <Button variant="ghost" onClick={handleGeneratePDF} disabled={admins.length === 0}>
            Gerar PDF
          </Button>
        </div>

        <AdminFilterBar chips={chips} resultCount={filteredAdmins.length} resultLabel="administradores">
          <AdminSearchField
            id="admin-search"
            label="Busca"
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="CPF, nome ou e-mail"
          />
        </AdminFilterBar>

        {loadingAdmins ? (
          <AdminLoadingState label="Carregando administradores..." />
        ) : pagedAdmins.length === 0 ? (
          <AdminEmptyState message="Nenhum administrador encontrado." />
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID Admin</th>
                    <th>CPF</th>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Nível de Usuário</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedAdmins.map((admin) => (
                    <tr key={admin.id_user}>
                      <td>{admin.id_user}</td>
                      <td>{admin.cpf}</td>
                      <td>{displayName(admin.name)}</td>
                      <td>{admin.email}</td>
                      <td>
                        <span className={`badge ${admin.user_level === 'super_admin' ? 'badge--success' : 'badge--neutral'}`}>
                          {admin.user_level}
                        </span>
                      </td>
                      <td>
                        <div className="admin-row-actions">
                          <button className="admin-row-action admin-row-action--edit" onClick={() => startEditing(admin)}>
                            Editar
                          </button>
                          <button className="admin-row-action admin-row-action--delete" onClick={() => handleDeleteAdmin(admin.id_user)}>
                            Apagar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AdminPagination page={adminPage} totalPages={totalAdminPages} onChange={setAdminPage} />
          </>
        )}
      </section>
    </AdminLayout>
  );
};

export default AdminPoloRegister;
